import { useState } from "react";

import { IgDialog, IgRadio, IgTextField } from "@intelligems/ig-theme";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
} from "@mui/material";
import Stack from "@mui/material/Stack";
import { useDialog, useDialogs } from "@src/hooks/dialog";
import { useSelector } from "@src/store";
import { ExperienceBuilderSelector } from "@src/store/slices/experienceBuilder";
import {
  mpVisualBuilderRevertFeedbackSubmitted,
  type VisualBuilderRevertFeedbackReason,
} from "@src/utils/mixpanel";

const REASONS: {
  value: VisualBuilderRevertFeedbackReason;
  label: string;
}[] = [
  { value: "missing_feature", label: "Missing a feature I need" },
  { value: "bug", label: "Ran into a bug" },
  { value: "prefer_old_ui", label: "Prefer the old UI" },
  { value: "other", label: "Other" },
];

export const RevertFeedbackDialog = () => {
  const dialog = useDialog("visualBuilderRevertFeedback");
  const { closeDialog } = useDialogs();
  const isReverting = useSelector(
    ExperienceBuilderSelector.getIsRevertingToLegacyEditor
  );

  const [reason, setReason] =
    useState<VisualBuilderRevertFeedbackReason>("missing_feature");
  const [notes, setNotes] = useState("");

  const onConfirm = dialog.props?.onConfirm;
  const trimmedNotes = notes.trim();
  const canSubmit = trimmedNotes.length > 0 && !isReverting;

  const close = () => {
    if (isReverting) {
      return;
    }
    closeDialog("visualBuilderRevertFeedback");
    setReason("missing_feature");
    setNotes("");
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    mpVisualBuilderRevertFeedbackSubmitted({ reason, comments: trimmedNotes });
    await onConfirm?.();
    closeDialog("visualBuilderRevertFeedback");
    setReason("missing_feature");
    setNotes("");
  };

  return (
    <IgDialog
      showCloseIcon
      dialogHeight={null}
      handleClose={close}
      showDialog={dialog.isOpen}
      title="Switch back to the legacy editor"
      subtitle="Before we switch you back, tell us what's not working. Your feedback directly shapes the new builder."
      maxWidth="sm"
      shouldNotClose
      loading={isReverting}
      noButtonText="Cancel"
      handleNoClick={close}
      yesButtonText="Submit & switch"
      handleYesClick={handleSubmit}
      yesButtonDisabled={!canSubmit}
      dontUseDialogContent
      rawContent={
        <Stack spacing={3} sx={{ px: 3, py: 1 }}>
          <FormControl>
            <FormLabel id="revert-feedback-reason-label">
              What&apos;s the main reason?
            </FormLabel>
            <RadioGroup
              aria-labelledby="revert-feedback-reason-label"
              name="revert-feedback-reason"
              value={reason}
              onChange={(e) =>
                setReason(e.target.value as VisualBuilderRevertFeedbackReason)
              }
            >
              {REASONS.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<IgRadio />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </FormControl>
          <IgTextField
            label="Tell us more"
            required
            placeholder="What's missing or not working for you?"
            multiline
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Stack>
      }
    />
  );
};
